import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Textarea } from '@/components/ui-kit/textarea';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { buildSystemRoleKey } from '@/lib/organization-roles';
import { useCreateRole, useUpdateRole } from '../../hooks/use-roles';
import { roleFormSchema, RoleFormValues } from '../../schemas/role-form-schema';
import { OrganizationRoleRow } from '../../types/role.types';

export interface RoleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The role to edit; none creates one. */
  row: OrganizationRoleRow | null;
}

/**
 * Creates a system role (offered in every organization) or edits a role's name and description. The
 * key is generated from the role's name on create (`team_lead`) and never changes afterwards.
 */
export const RoleFormDialog = ({ open, onOpenChange, row }: RoleFormDialogProps) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(row);
  const { mutateAsync: createRole, isPending: isCreating } = useCreateRole();
  const { mutateAsync: updateRole, isPending: isUpdating } = useUpdateRole();
  const isSubmitting = isCreating || isUpdating;

  const form = useForm<RoleFormValues>({
    resolver: async (data, context, options) => {
      const result = await zodResolver(roleFormSchema)(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: { Name: '', Description: '' },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({ Name: row?.role.name ?? '', Description: row?.role.description ?? '' });
  }, [open, row, form]);

  const name = useWatch({ control: form.control, name: 'Name' });
  const key = isEditMode ? (row?.role.slug ?? '') : buildSystemRoleKey(name ?? '');

  const handleSubmit = async (values: RoleFormValues) => {
    const input = { name: values.Name.trim(), description: values.Description?.trim() ?? '' };
    try {
      if (row) {
        await updateRole({ slug: row.role.slug, input });
      } else {
        if (!key) return;
        const result = await createRole({ slug: key, ...input });
        if (result !== 'created') return;
      }
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditMode ? t('EDIT_ROLE') : t('CREATE_ROLE')}</DialogTitle>
          {isEditMode && <DialogDescription>{t('ROLE_KEY_LOCKED')}</DialogDescription>}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="Name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('ROLE_NAME')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('ROLE_NAME')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormItem>
              <FormLabel>{t('ROLE_KEY')}</FormLabel>
              <Input value={key} placeholder={t('ROLE_KEY')} disabled />
              <FormDescription>{t('ROLE_KEY_AUTO_GENERATED')}</FormDescription>
            </FormItem>

            <FormField
              control={form.control}
              name="Description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('DESCRIPTION')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} placeholder={t('DESCRIPTION')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting || (!isEditMode && !key)}
              >
                {isEditMode ? t('SAVE_CHANGES') : t('CREATE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
